'use client';

import React, { useState } from 'react';
import useLearnMode from './useLearnMode';

/** Typography tokens (exact per spec) */
const type = {
  caption:
    "text-[14px] leading-[21px] font-[400] [font-family:'Raveo_Display',sans-serif]",
  h2:
    "md:text-[40px] text-[28px] leading-[50px] font-[600] [font-family:'Raveo_Display',sans-serif] text-[#022850]",
  rowTitle:
    "text-[18px] leading-[27px] font-[600] [font-family:'Raveo_Display',sans-serif]",
  lead:
    "text-[16px] leading-[24px] font-[400] [font-family:'Raveo_Display',sans-serif]",
  bullet:
    "text-[14px] leading-[21px] font-[400] [font-family:'Raveo_Display',sans-serif]",
};

const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M4 11L6.5 13.5M10 9.5L12.5 7M8 11L10.5 13.5L16.5 7"
      stroke="#4A3CF3"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default function LearnMode() {
  const { UI, items } = useLearnMode();
  const [active, setActive] = useState(items[0].id);
  const activeItem = items.find(i => i.id === active)!;

  return (
    <section id='benefits' className="w-full">
      {/* Scale/1600, side padding 120 */}
      <div className="mx-auto max-w-[1600px] px-7 md:px-[120px] py-16 md:py-24">
        <div className="grid grid-cols-1 items-start gap-10 md:grid-cols-[minmax(0,1fr)_568px] md:gap-16">
          {/* LEFT — Accordion like Level component */}
          <div>
            {/* Caption pill */}
            <div
              className={`inline-flex bg-[#FBFCF2] text-[#4A3CF3] items-center gap-[6px] rounded-[36px] border px-3 py-1 ${type.caption}`}
            
            >
              Learning Modes
            </div>

            {/* Title */}
            <h2 className={`mt-6 ${type.h2}`}>
              Choose Your<br /> Learning Style
            </h2>

            {/* Rows (accordion) */}
            <div className="mt-6">
              {items.map((m, idx) => {
                const isActive = m.id === active;
                return (
                  <div key={m.id} className="py-3">
                    {/* Header */}
                    <button
                      type="button"
                      onClick={() => setActive(m.id)}
                      className="group flex w-full items-center justify-between"
                      aria-expanded={isActive}
                      aria-controls={`mode-${m.id}`}
                    >
                      <span
                        className={`${type.rowTitle} transition-colors`}
                        style={{ color: isActive ? UI.brand : UI.secondary }}
                      >
                        {m.title}
                      </span>
                    </button>

                    {/* Animated content (like Level component) */}
                    <div
                      id={`mode-${m.id}`}
                      className={[
                        "grid transition-[grid-template-rows] duration-300 ease-out",
                        isActive ? "grid-rows-[1fr] mt-3" : "grid-rows-[0fr]",
                      ].join(" ")}
                    >
                      <div className="overflow-hidden">
                        <p className={`${type.lead}`} style={{ color: UI.tertiary }}>
                          {m.lead}
                        </p>

                        <ul className="mt-3 space-y-3">
                          {m.bullets.map((b, i) => (
                            <li key={i} className="flex items-start gap-3">
                              <span className="mt-[2px] shrink-0"><CheckIcon /></span>
                              <span className={`${type.bullet}`} style={{ color: UI.tertiary }}>
                                {b}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Divider (same style as other sections) */}
                    {idx < items.length - 1 && (
                      <div className="mt-6 h-px w-full" style={{ background: UI.captionBorder }} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* RIGHT — Image swaps with active row */}
          <div
            className="relative flex w-[568px] max-w-full h-[436px] justify-center items-center shrink-0 rounded-[24px] overflow-hidden"
            style={{ aspectRatio: '142/109' }}
          >
            <img
              key={activeItem.image}
              src={activeItem.image}
              alt={activeItem.alt}
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
